'use client'

import * as React from 'react'
import { useSyncCurrencyCookie } from '@repo/cf-user/hooks/use-sync-currency-cookie'
import type { UserQuery } from '@repo/cf-user/gen/user'
import { userQueryKey, userQueryFetcher } from 'auth/hooks'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

export const queryClientDefaultOptions = {
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000,
    },
  },
}

function makeQueryClient() {
  return new QueryClient(queryClientDefaultOptions)
}

let browserQueryClient: QueryClient | undefined = undefined

function getQueryClient() {
  if (typeof window === 'undefined') {
    // Server: always make a new query client
    // oxlint-disable-next-line no-else-return
    return makeQueryClient()
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) {
      browserQueryClient = makeQueryClient()
    }
    return browserQueryClient
  }
}

export const PrefetchCFUser = () => {
  const queryClient = getQueryClient()
  queryClient.prefetchQuery<UserQuery>({
    queryKey: userQueryKey,
    queryFn: userQueryFetcher,
  })

  return null
}

function onCookieSync() {
  window.location.reload()
}

function CurrencyCookieSync({ children }: React.PropsWithChildren) {
  useSyncCurrencyCookie({ onSync: onCookieSync })
  return children
}

export function ReactQueryProvider({ children }: React.PropsWithChildren) {
  // NOTE: Avoid useState when initializing the query client if you don't
  //       have a suspense boundary between this and the code that may
  //       suspend because React will throw away the client on the initial
  //       render if it suspends and there is no boundary
  const queryClient = getQueryClient()

  return (
    <QueryClientProvider client={queryClient}>
      <CurrencyCookieSync>{children}</CurrencyCookieSync>
      {process.env.NODE_ENV === 'development' ? <ReactQueryDevtools initialIsOpen={false} /> : null}
    </QueryClientProvider>
  )
}
